import React, { useCallback, useEffect, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useTranslation } from 'react-i18next'
import { datesAreOnSameDay } from '../../utils/util'
import { format_group_day } from '../../utils/format_day'
import BigImageModal from '../modals/BigImageModal'
import ArchiveFilters from './ArchiveFilters'
import ArchiveSearch from './ArchiveSearch'
import ArchiveItem from './ArchiveItem'
import ArchiveMascot from './ArchiveMascot'
import ArchiveResultModal from './ArchiveResultModal'
import ArchiveSkeleton, { ArchiveCardSkeleton } from './ArchiveSkeleton'
import { ArchiveViewerContext } from './ArchiveBodies'
import { readCounts, readFirstPage, writeCounts, writeFirstPage } from './archiveCache'
import '../../styles/Archive.css'

const PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 300
const SKELETON_DELAY_MS = 150

const EMPTY_MESSAGE_KEYS = {
    self: {
        all: 'archiveEmptyAll',
        mood: 'archiveEmptyMood',
        poll: 'archiveEmptyPoll',
        question: 'archiveEmptyQuestion',
        survey: 'archiveEmptySurvey',
        journal: 'archiveEmptyJournal',
    },
    staff: {
        all: 'archiveStaffEmptyAll',
        mood: 'archiveStaffEmptyFiltered',
        poll: 'archiveStaffEmptyFiltered',
        question: 'archiveStaffEmptyFiltered',
        survey: 'archiveStaffEmptyFiltered',
        journal: 'archiveStaffEmptyFiltered',
    },
}

// Polls and questions are upserted, so an edited answer can reappear on a later page; keep the first.
const appendUnique = (current, incoming) => {
    const seen = new Set(current.map((item) => item.id))
    return current.concat(incoming.filter((item) => !seen.has(item.id)))
}

function ArchiveState({ variant = 'empty', title, message, action }) {
    return (
        <div className="archive-state" role={variant === 'error' ? 'alert' : 'status'}>
            <ArchiveMascot />
            {!!title && <p className="archive-state-title">{title}</p>}
            {!!message && <p className="archive-state-text">{message}</p>}
            {!!action && (
                <button type="button" className="archive-state-action" onClick={action.onClick}>
                    {action.label}
                </button>
            )}
        </div>
    )
}

/**
 * A day-grouped, searchable, filterable archive timeline.
 *
 * - `fetchPage({ type, before, limit, q })` returns `{ status, data }` from the archive API.
 * - `cacheKey` identifies whose archive this is, so reopening it renders instantly.
 * - `viewer` is 'self' (your own answers) or 'staff' (a teacher viewing a student).
 * - `scrollId` must be unique on the page; the list scrolls inside that element.
 */
export default function ArchiveList({ fetchPage, cacheKey, viewer = 'self', scrollId = 'archive-scroll', onGoToTasks, className = '' }) {
    const { t, i18n } = useTranslation()

    const [filter, setFilter] = useState('all')
    const [searchText, setSearchText] = useState('')
    const [query, setQuery] = useState('')
    const cached = readFirstPage(cacheKey, 'all')
    const [items, setItems] = useState(cached?.items || [])
    const [cursor, setCursor] = useState(cached?.next_cursor || null)
    const [counts, setCounts] = useState(() => readCounts(cacheKey))
    // loading: first page with nothing to show · refreshing: new search over the current list · idle · error
    const [status, setStatus] = useState(cached ? 'idle' : 'loading')
    const [loadMoreFailed, setLoadMoreFailed] = useState(false)
    const [showSkeleton, setShowSkeleton] = useState(false)
    const [selectedImage, setSelectedImage] = useState('')
    const [detailItem, setDetailItem] = useState(null)

    // Each request carries the generation it was made for; responses from an older one
    // (filter or search changed meanwhile) are dropped. This axios version cannot abort.
    const generation = useRef(0)
    const loadingMore = useRef(false)
    const fetchRef = useRef(fetchPage)
    fetchRef.current = fetchPage

    useEffect(() => {
        const timer = setTimeout(() => setQuery(searchText.trim()), SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [searchText])

    // Avoid flashing a skeleton for requests that resolve almost instantly.
    useEffect(() => {
        if (status !== 'loading') {
            setShowSkeleton(false)
            return undefined
        }
        const timer = setTimeout(() => setShowSkeleton(true), SKELETON_DELAY_MS)
        return () => clearTimeout(timer)
    }, [status])

    const loadFirstPage = useCallback(
        async (nextFilter, nextQuery) => {
            const requestGeneration = ++generation.current
            loadingMore.current = false
            setLoadMoreFailed(false)

            const fromCache = nextQuery ? null : readFirstPage(cacheKey, nextFilter)
            if (fromCache) {
                setItems(fromCache.items)
                setCursor(fromCache.next_cursor)
                setStatus('idle')
            } else {
                setStatus((current) => (nextQuery && current !== 'error' ? 'refreshing' : 'loading'))
            }

            const resp = await fetchRef.current({ type: nextFilter, limit: PAGE_SIZE, q: nextQuery })
            if (requestGeneration !== generation.current) return

            if (resp?.status === 200 && Array.isArray(resp.data?.items)) {
                const page = { items: resp.data.items, next_cursor: resp.data.next_cursor || null }
                if (!nextQuery) writeFirstPage(cacheKey, nextFilter, page)
                setItems(page.items)
                setCursor(page.next_cursor)
                setStatus('idle')
                if (resp.data.counts) {
                    writeCounts(cacheKey, resp.data.counts)
                    setCounts(resp.data.counts)
                }
            } else if (!fromCache) {
                // A cached page stays on screen if revalidation fails; only an empty screen shows the error.
                setStatus('error')
            }
        },
        [cacheKey]
    )

    const loadMore = useCallback(async () => {
        if (!cursor || loadingMore.current) return
        loadingMore.current = true
        setLoadMoreFailed(false)
        const requestGeneration = generation.current

        const resp = await fetchRef.current({ type: filter, before: cursor, limit: PAGE_SIZE, q: query })
        if (requestGeneration !== generation.current) return
        loadingMore.current = false

        if (resp?.status === 200 && Array.isArray(resp.data?.items)) {
            setItems((current) => appendUnique(current, resp.data.items))
            setCursor(resp.data.next_cursor || null)
        } else {
            setLoadMoreFailed(true)
        }
    }, [cursor, filter, query])

    useEffect(() => {
        if (cacheKey) loadFirstPage(filter, query)
    }, [filter, query, cacheKey, loadFirstPage])

    useEffect(() => {
        document.getElementById(scrollId)?.scrollTo({ top: 0 })
    }, [filter, query, scrollId])

    const openImageBig = useCallback((url) => setSelectedImage(url), [])
    const openDetail = useCallback((item) => setDetailItem(item), [])

    const isEmpty = status === 'idle' && items.length === 0
    const hasList = (status === 'idle' || status === 'refreshing') && items.length > 0
    const isSearching = !!searchText.trim() && (searchText.trim() !== query || status === 'refreshing')
    const messages = EMPTY_MESSAGE_KEYS[viewer] || EMPTY_MESSAGE_KEYS.self

    return (
        <ArchiveViewerContext.Provider value={viewer}>
            <div className={`archive ${className}`}>
                <div className="archive-controls">
                    <ArchiveSearch value={searchText} onChange={setSearchText} busy={isSearching} />
                    <ArchiveFilters value={filter} counts={counts} showCounts={!query} onChange={setFilter} />
                </div>

                <div id={scrollId} className="archive-scroll" aria-busy={status === 'loading' || status === 'refreshing'}>
                    {status === 'loading' && showSkeleton && <ArchiveSkeleton />}

                    {status === 'error' && (
                        <ArchiveState
                            variant="error"
                            title={t('archiveErrorTitle')}
                            message={t('archiveErrorMessage')}
                            action={{ label: t('archiveTryAgain'), onClick: () => loadFirstPage(filter, query) }}
                        />
                    )}

                    {isEmpty && !!query && (
                        <ArchiveState
                            title={t('archiveNoResultsTitle', { query })}
                            message={t('archiveNoResultsMessage')}
                            action={{ label: t('archiveSearchClear'), onClick: () => setSearchText('') }}
                        />
                    )}

                    {isEmpty && !query && (
                        <ArchiveState
                            title={viewer === 'staff' ? t('archiveStaffEmptyTitle') : filter === 'all' ? t('archiveEmptyTitle') : t('archiveEmptyFilteredTitle')}
                            message={t(messages[filter])}
                            action={onGoToTasks ? { label: t('archiveGoToTasks'), onClick: onGoToTasks } : undefined}
                        />
                    )}

                    {hasList && (
                        <InfiniteScroll
                            dataLength={items.length}
                            next={loadMore}
                            hasMore={!!cursor && !loadMoreFailed}
                            scrollableTarget={scrollId}
                            style={{ overflow: 'visible' }}
                            loader={<ArchiveCardSkeleton />}
                        >
                            {/* Keyed by filter and search so a new result set replays the entrance stagger. */}
                            <div key={`${filter}:${query}`} className={`archive-list${status === 'refreshing' ? ' is-refreshing' : ''}`}>
                                {items.map((item, index) => {
                                    const previous = items[index - 1]
                                    const newDay = !previous || !datesAreOnSameDay(new Date(item.created_date), new Date(previous.created_date))
                                    return (
                                        <React.Fragment key={item.id}>
                                            {newDay && (
                                                <div className="archive-day">
                                                    <span className="archive-day-label">{format_group_day(item.created_date, i18n.language)}</span>
                                                </div>
                                            )}
                                            <ArchiveItem item={item} index={index} openImageBig={openImageBig} onOpenDetail={openDetail} />
                                        </React.Fragment>
                                    )
                                })}
                            </div>
                        </InfiniteScroll>
                    )}

                    {loadMoreFailed && (
                        <div className="archive-inline-error" role="alert">
                            <span>{t('archiveLoadMoreError')}</span>
                            <button type="button" className="archive-inline-retry" onClick={loadMore}>
                                {t('archiveTryAgain')}
                            </button>
                        </div>
                    )}

                    {status === 'idle' && !cursor && items.length > 3 && <p className="archive-end">{t('archiveEnd')}</p>}
                </div>

                <ArchiveResultModal item={detailItem} onClose={() => setDetailItem(null)} openImageBig={openImageBig} />
                <BigImageModal modalIsOpen={!!selectedImage} closeModal={() => setSelectedImage('')} selectedImage={selectedImage} />
            </div>
        </ArchiveViewerContext.Provider>
    )
}
